import React from "react";
import { CollapseFAQ, H3, NamedLink } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';

import ContinueIcon from '../../TopbarContainer/Topbar/images/continue-icon.svg';
import ParisMetro from '../images/France - Paris - Metro station.jpg'
import ParisPatisseries from '../images/France - Paris - Patisseries.jpg';
import Gardens from '../images/gardens.jpg';

import css from '../HouseSittingAbroadPage.module.css';
import { europeFaq } from "./faqs";

const EuropePage = props => {
    return (
        <div className={css.root}>
            <H3>
                House Sitting in Europe
            </H3>
            <p className={css.info}>
                House sitting in Europe combines affordable travel with authentic living experiences. In exchange for caring for a property and often taking care of pets, house sitters enjoy free accommodation in unique destinations.
            </p>
            <br />
            <p className={css.info}>
                This arrangement creates a win-win scenario for travellers and homeowners alike, offering both cost savings and trusted companionship for pets.
            </p>
            <br />
            <p className={css.info}>
                From historic cities to tranquil rural settings, Europe's house sitting opportunities provide a perfect way to immerse yourself in local cultures while exploring the continent in comfort.
            </p>

            <NamedLink
                className={css.ctaButton}
                name="SignupPage"
            >
                <FormattedMessage id="HouseSittingAbroadPage.joinNow" />
                <img src={ContinueIcon} className={css.icon} />
            </NamedLink>

            <div className={css.imageContainerVertical}>
                <img className={css.image} src={ParisMetro} alt='Travelling in Europe is so much cheaper when you housesit' />
                <p className={css.info}>
                    Travelling in Europe is so much cheaper when you housesit
                </p>
            </div>

            <p className={css.subtitle}>
                Why Choose HouseSit Match?
            </p>
            <p className={css.info}>
                HouseSit Match is your trusted partner for house sitting in Europe. Our platform simplifies the process, connecting verified homeowners with experienced sitters through a secure, user-friendly system. With transparent membership options and excellent customer support, we provide the tools to make your house sitting journey successful and stress-free
            </p>

            <p className={css.subtitle}>
                The Benefits of House Sitting in Europe
            </p>
            <p className={css.info}>
                House sitting in Europe comes with numerous advantages:
            </p>

            <ul className={css.list}>
                <li>
                    <strong>Affordable Accommodation: </strong>
                    Save on expensive hotel stays.
                </li>
                <li>
                    <strong>Cultural Immersion: </strong>
                    Experience daily life in local neighbourhoods.
                </li>
                <li>
                    <strong>Pet Companionship: </strong>
                    Bond with pets during your stay.
                </li>
                <li>
                    <strong>Flexible Travel: </strong>
                    Choose short or long-term assignments to suit your schedule.
                </li>
                <li>
                    <strong>Unique Experiences: </strong>
                    Stay in diverse settings, from countryside retreats to city apartments.
                </li>
            </ul>

            <p className={css.subtitle}>
                Types of House Sitting Arrangements in Europe
            </p>

            <p className={css.info}>
                Depending on your preferences, you can choose from a variety of arrangements:
            </p>

            <ul className={css.list}>
                <li>
                    <strong>Short-Term Assignments: </strong>
                    Ideal for weekend travelers or those exploring nearby locations.
                </li>
                <li>
                    <strong>Long-Term Stays: </strong>
                    Suitable for slow travelers or digital nomads.
                </li>
                <li>
                    <strong>Pet Sitting Focused: </strong>
                    For those who enjoy caring for animals.
                </li>
                <li>
                    <strong>Luxury House Sitting: </strong>
                    Assignments in high-end properties with unique amenities.
                </li>
                <li>
                    <strong>Seasonal Sits: </strong>
                    Opportunities during peak travel periods or holidays.
                </li>
            </ul>

            <div className={css.imageContainerHorizontal}>
                <img className={css.image} src={Gardens} alt="Select your house sits to enjoy seasonal travel and its benefits" />
                <p className={css.info}>
                    Select your house sits to enjoy seasonal travel and its benefits
                </p>
            </div>

            <p className={css.subtitle} style={{ marginBottom: 0 }}>
                Popular Locations for House Sitting in Europe
            </p>

            <ul className={css.listNumbers} style={{ marginTop: 0 }}>
                <li>
                    <strong>France: </strong>
                    Enjoy charming countryside homes or urban escapes in Paris.
                </li>
                <li>
                    <strong>Italy: </strong>
                    Care for villas amidst Tuscany's vineyards or seaside retreats along the Amalfi Coast.
                </li>
                <li>
                    <strong>United Kingdom: </strong>
                    Explore historic cities like London or peaceful villages in the Cotswolds.
                </li>
                <li>
                    <strong>Spain: </strong>
                    Combine pet care with sunny stays in Barcelona or Andalusian villages.
                </li>
                <li>
                    <strong>Germany: </strong>
                    Experience life in bustling Berlin or serene Bavarian towns.
                </li>
            </ul>

            <p className={css.subtitle}>
                Costs of House Sitting in Europe
            </p>
            <p className={css.info}>
                House sitting itself typically involves no monetary exchange, as accommodation is provided in return for property and pet care. However, some costs to consider include:
            </p>

            <ul className={css.list}>
                <li>
                    <strong>Travel Expenses: </strong>
                    Flights, trains, or local transport to the assignment location.
                </li>
                <li>
                    <strong>Living Costs: </strong>
                    Groceries and daily necessities during your stay.
                </li>
                <li>
                    <strong>Platform Memberships: </strong>
                    Affordable annual fees for accessing house sitting opportunities on trusted sites like HouseSit Match.
                </li>
            </ul>

            <p className={css.subtitle}>
                Legal Considerations
            </p>

            <p className={css.info}>
                Before house sitting in Europe, it's essential to understand the legal requirements:
            </p>
            <br />
            <p className={css.info}>
                <strong>
                    Visa Requirements:
                </strong>
                {" "}Non-EU citizens may need a tourist or Schengen visa for stays under 90 days. For longer stays, a residence permit might be necessary. Always check the specific requirements for your destination country.
            </p>
            <p className={css.info}>
                <strong>
                    Pet Care Regulations:
                </strong>
                {" "}Some countries require specific vaccinations or certifications for pets, especially if you're traveling with them across borders.
            </p>
            <p className={css.info}>
                <strong>
                    Homeowner Contracts:
                </strong>
                {" "}Ensure clear agreements outlining responsibilities and expectations, including pet care, property maintenance, and emergency contacts.
            </p>
            <div className={css.imageContainerHorizontal}>
                <img className={css.image} src={ParisPatisseries} alt="Select your housesits by the cultures you want to discover" />
                <p className={css.info}>
                    Select your housesits by the cultures you want to discover
                </p>
            </div>

            <p className={css.subtitle}>
                How to Become a House Sitter in Europe
            </p>
            <p className={css.info}>
                Follow these simple steps to start your house sitting journey in Europe:
            </p>

            <ul className={css.listNumbers}>
                <li>
                    <strong>Sign Up on a Trusted Platform: </strong>
                    Create a profile on HouseSit Match.
                </li>
                <li>
                    <strong>Complete Your Profile: </strong>
                    Include references, past experience, and a compelling bio.
                </li>
                <li>
                    <strong>Search for Assignments: </strong>
                    Filter by location, duration, or specific preferences.
                </li>
                <li>
                    <strong>Apply for Opportunities: </strong>
                    Tailor your applications to match homeowner needs.
                </li>
                <li>
                    <strong>Communicate Clearly: </strong>
                    Discuss expectations upfront to build trust.
                </li>
                <li>
                    <strong>Prepare for Your Trip: </strong>
                    Arrange travel, insurance, and any necessary documents.
                </li>
            </ul>

            <H3>
                FAQs
            </H3>

            <CollapseFAQ
                list={europeFaq}
            />

            <p className={css.subtitle}>
                Testimonials
            </p>
            <p className={css.info}>
                Include real testimonials from sitters who have successfully completed house sitting assignments in Canada.
            </p>

            <p className={css.subtitle}>
                Make House Sitting in Europe Your Next Adventure
            </p>
            <p className={css.info}>
                House sitting in Europe opens the door to unforgettable travel experiences. Whether you're caring for a cosy countryside cottage or a chic city apartment, it's an enriching way to explore the continent while offering valuable help to homeowners.            </p>
            <br />
            <p className={css.info}>
                Ready to begin?{" "}
                <NamedLink name="SignupPage">Join HouseSit Match today</NamedLink>
                {" "}and discover how house sitting can transform your European journey.
            </p>

            <NamedLink
                className={css.ctaButton}
                name="SignupPage"
            >
                <FormattedMessage id="HouseSittingAbroadPage.joinNow" />
                <img src={ContinueIcon} className={css.icon} />
            </NamedLink>
        </div>
    )
};

export default EuropePage;